// 丰富细节
import { redrawWeightDefault, defaultAspectRatios } from '../default';
import type { LayoutReferState } from '@/types/image';
import type { Tag } from '@/types/prompt';
import { getModelEventTrackType, mapModelSource } from '../index';

export const module = ModuleId.Colour;

export const colourParams = {
  module,
  source: 1,// 来源：1-直接生成，2-再迭代
  advancedMode: false, // 高级模式 (已弃用)
  redrawWeight: redrawWeightDefault, // 重绘幅度
  batchRedraw: false, // 重绘幅度批量验证
  imgTags: [] as Tag[], // 图像识别标签
  prompt: '', // 用户输入的提示词
  negativePrompt: '', // 反向提示词
  negativePromptWeight: 0.4, // 反向提示词权重
  layoutRefer: null as null | LayoutReferState, // 原图
  maskImage: null as null | LayoutReferState, // 原图蒙板图
  maskContrastImage: null as null | LayoutReferState, // 原图蒙板图反相图
  layoutReferWeight: 0.5, // 布局参考图权重
  faceDetail: true, // 面部精修
  aspectRatios: defaultAspectRatios, // 尺寸比例
  batchSize: 1, // 生成数量 固定1
}


import { useArtworkStore } from '@/store/artwork';
import { useUserStore } from '@/store/user';
import { filteringInvalidFields } from '@/utils';
import { ModuleRoute } from '@/types/artwork/generate';

// 将当前用户输入的生图参数转换成后端所需要
function getColourGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  let params = {
    module: colourParams.module,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    modelList: artworkStore.artworkState.modelList,
    loraList: artworkStore.artworkState.modelList.map(item => {
      return {
        modelId: item.id,
        strength: item.strength
      }
    }),
  } as any;

  params = {
    ...artworkStore.colour.params,
    ...params,
    maskId: artworkStore.colour.params.maskContrastImage?.id || null,
    aspectRatios: null,
    layoutReferId: artworkStore.colour.params.layoutRefer?.id || null,
    batchSize: 1
  };

  // 使用HOLO - V1模型
  if (params.modelList[0]?.ckptType === 'Flux') {
    // 关闭面部精修
    params.faceDetail = false;

    // 移除负向描述词
    params.negativePrompt = '';
  }

  // 图片提示词
  params.imgTag = artworkStore.colour.params.imgTags.map(item => item.enName).join(',');

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ['id', 'width', 'height', 'image']);

  return params;
}

import { useEventTracking } from '@/hooks/useEventTracking';
import type { ModelState } from '@/types/model';
import { startModuleGenerate } from './index';
import { useModelStore } from '@/store/model';
import { styleModelOptions } from '@/constants';
import { getModuleLabelById } from '@xmiles/holopix-helper';
import { ModuleId } from '@xmiles/holopix-types';

// 开启模块生图
export async function startColourGenerate(oldParams?: any, options?: {
  onClientIdGenerated?: (clientId: string) => void;
  onClientIdCleared?: () => void;
}) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();
  const params = getColourGenerateParam();
  const module = colourParams.module;
  const modelList = artworkStore.artworkState.modelList || [];
  const modelLabels = modelList.flatMap(item => item.label || []);
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item) => item!.label);

  const modelCategories = modelList.map((item) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item) => item!.label);


  const isUserGeneratedText = (() => {
    if (modelList.every(i => i.userId !== 0)) return '是';
    if (modelList.every(i => i.userId === 0)) return '否';
    return '混合';
  })();
  const modelIdList = params.modelList.map((item: ModelState) => item.id);
  const modelNameList = params.modelList.map((item: ModelState) => item.modelName);
  // 创建loraType映射关系
  const loraTypeMap = Object.fromEntries(styleModelOptions.map((option) => [option.value, option.label]));
  // 提取loraType对应的label组成数组
  const loraTypeLabels = modelList.map((item) => item.loraType ? loraTypeMap[item.loraType] || '视频模型' : item.loraType);

  startModuleGenerate(
    module,
    {
      getModuleParams: () => {
        return oldParams || params;
      },
      onStartTrack: (clientId) => {
        reportStartEventTracking(clientId);
      },
      onFinishTrack: (data) => {
        reportFinishEventTracking(data);
      },
      onClientIdGenerated: options?.onClientIdGenerated,
      onClientIdCleared: options?.onClientIdCleared,
    }
  );


  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {


    useEventTracking({
      event: 'creation_event',
      eventValue: {
        client_id: clientId,
        module: getModuleLabelById(module) as string,
        source: params.source === 1 ? '直接生成' : '再迭代',
        redrawWeight: params.redrawWeight,
        artwork_id: artworkStore.artworkState.id.toString(),
        prompt: params.prompt,
        negative_prompt: params.negativePrompt,
        face_detail: params.faceDetail ? 1 : 0,
        batchSize: params.batchSize,
        hp_model_id: String(modelIdList.join(',')),
        // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
        model_name: modelNameList.join(',') || JSON.stringify(params.modelList || ['--']),
        ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(','),
        lora_type: params.modelList.map((item: ModelState) => item.loraType).join(','),
        is_personal_model: getModelEventTrackType(params.modelList),
        model_tags: modelTagList.join(','),
        model_type: modelTypeList.join(','),

        hp_model_id_list: modelIdList,
        hp_model_name_list: modelNameList,
        hp_model_tags_list: modelTagList,
        hp_model_type_list: modelTypeList,
        hp_is_user_generated: isUserGeneratedText,
        is_composite_model: loraTypeLabels

      }
    });
  }

  function reportFinishEventTracking(
    {
      client_id = '',
      result = 0 as 0 | 1,
      imageIds = '',
      queue_time_number = 0,
      loading_time_number = 0,
      vipChannel = false,
    } = {}) {
    useEventTracking({
      event: 'artwork_status_event',
      eventValue: {
        module: getModuleLabelById(module) as string,
        client_id,
        source: params.source === 1 ? '直接生成' : '再迭代',
        img_id: imageIds,
        result,
        queue_time_number,
        loading_time_number,
        hp_model_id: String(modelIdList.join(',')),
        hp_model_id_list: modelIdList,
        // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
        model_name: params.modelList.map((item: ModelState) => item.modelName).join(',') || JSON.stringify(params.modelList || ['--']),
        ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(','),
        first_model_ckpt: params.modelList[0]?.ckptType,
        vipChannel: vipChannel,
        lora_type: params.modelList.map((item: ModelState) => item.loraType).join(','),
        is_personal_model: getModelEventTrackType(params.modelList),
        model_source: mapModelSource(getModelEventTrackType(params.modelList)),
        model_id: String(artworkStore.artworkState.modelList.map((item: ModelState) => item.id).join(',')),
        prompt: params.prompt,
        negative_prompt: params.negativePrompt,
        face_detail: params.faceDetail ? 1 : 0,
        batchRedraw: params.batchRedraw ? 0 : 1,
        redrawWeight: params.redrawWeight,
        model_tags: modelTagList.join(','),
        hp_model_tags_list: modelTagList,
        model_type: modelTypeList.join(','),
        hp_model_type_list: modelTypeList,
        hp_is_user_generated: isUserGeneratedText
      }
    });
  }
}